import { motion } from "framer-motion";
import {
    Instagram,
    Music2,
    Youtube,
    Linkedin,
    MessageCircle,
    Phone,
    Facebook,
    ArrowUpRight,
} from "lucide-react";
import { socials, socialHandles } from "../../lib/socials";
import { useI18n } from "../../lib/i18n";

const cardVariant = {
    hidden: { opacity: 0, y: 30 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function FollowMe() {
    const { t } = useI18n();
    const s = t.follow;

    const cards = [
        { key: "instagram", label: "Instagram", handle: socialHandles.instagram, href: socials.instagram, Icon: Instagram, color: "#E1306C", testid: "follow-instagram" },
        { key: "youtube", label: "YouTube", handle: socialHandles.youtube, href: socials.youtube, Icon: Youtube, color: "#FF0033", testid: "follow-youtube" },
        { key: "whatsapp", label: "WhatsApp", handle: socialHandles.whatsapp, href: socials.whatsapp, Icon: MessageCircle, color: "#25D366", testid: "follow-whatsapp" },
        { key: "tiktok", label: "TikTok", handle: socialHandles.tiktok, href: socials.tiktok, Icon: Music2, color: "#25F4EE", testid: "follow-tiktok" },
        { key: "facebook", label: "Facebook", handle: socialHandles.facebook, href: socials.facebook, Icon: Facebook, color: "#1877F2", testid: "follow-facebook" },
        { key: "linkedin", label: "LinkedIn", handle: socialHandles.linkedin, href: socials.linkedin, Icon: Linkedin, color: "#0A66C2", testid: "follow-linkedin" },
        { key: "viber", label: "Viber", handle: socialHandles.viber, href: socials.viber, Icon: Phone, color: "#7360F2", testid: "follow-viber" },
    ];

    return (
        <section id="follow" className="section relative bg-[#050505]" data-testid="follow-section">
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-16">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl font-light tracking-[-0.03em] leading-[1.02]">
                            {s.titlePre}
                            <span className="italic font-extralight text-white/60">{s.titleAccent}</span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <p className="font-body text-white/55 max-w-sm text-base leading-relaxed">{s.lede}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="follow-grid">
                    {cards.map((c, i) => {
                        const Icon = c.Icon;
                        return (
                            <motion.a key={c.key} href={c.href} target="_blank" rel="noopener noreferrer" custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={cardVariant} whileHover={{ y: -6 }} className="group relative rounded-3xl border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-transparent aspect-square p-6 flex flex-col justify-between overflow-hidden" data-testid={c.testid}>
                                <div aria-hidden className="pointer-events-none absolute -bottom-20 -right-20 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: `radial-gradient(circle at center, ${c.color}44, transparent 60%)`, filter: "blur(20px)" }} />
                                <div className="relative flex items-start justify-between">
                                    <div className="w-11 h-11 rounded-2xl bg-white/[0.05] border border-white/10 flex items-center justify-center text-white/85 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/40 transition-colors duration-500">
                                        <Icon size={18} strokeWidth={1.6} />
                                    </div>
                                    <ArrowUpRight size={16} className="text-white/30 group-hover:text-[#E55B13] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                                </div>
                                <div className="relative">
                                    <div className="font-heading text-lg md:text-xl font-medium tracking-[-0.01em] text-white">{c.label}</div>
                                    <div className="mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.16em] text-white/50 truncate">{c.handle}</div>
                                </div>
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
